import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Share2, Copy, Check, ImageDown, Smartphone, Lock, Sparkles } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { getPersonality, sharePersona, getCreatorId, createCheckout } from "../lib/api";
import { useUnlocks } from "../lib/unlocks";
import { PERSONA_ONLY_INCLUDES } from "../lib/plans";
import Logo from "../components/Logo";
import PersonaShareCard from "../components/PersonaShareCard";

export default function Personality() {
  const { has } = useUnlocks();
  const hasDeep = has("persona_only") || has("pro_pass");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingFormat, setExportingFormat] = useState(null); // "social" | "story" | null
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);
  const socialRef = useRef(null);
  const storyRef = useRef(null);

  useEffect(() => {
    (async () => {
      try { setData(await getPersonality()); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleShare = async () => {
    setSharing(true);
    try {
      const res = await sharePersona(getCreatorId());
      const url = `${window.location.origin}${res.url_path}`;
      setShareUrl(url);
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success("Link copied to clipboard");
        setTimeout(() => setCopied(false), 2500);
      } catch {
        toast.success("Link created — copy it below");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sharing failed");
    } finally {
      setSharing(false);
    }
  };

  const copyExisting = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.warn("Clipboard write failed:", err);
      toast.error("Couldn't copy — select the URL and copy it manually");
    }
  };

  const exportCard = async (format) => {
    const node = format === "story" ? storyRef.current : format === "social" ? socialRef.current : cardRef.current;
    if (!node) return;
    setExportingFormat(format);
    try {
      const dataUrl = await toPng(node, {
        cacheBust: true,
        pixelRatio: format === "social" || format === "story" ? 1 : 2,
        backgroundColor: "#050505",
      });
      const safeName = (data.personality_type || "persona").toLowerCase().replace(/\s+/g, "-");
      const suffix = format === "story" ? "story-1080x1920" : format === "social" ? "social-1200x630" : "card";
      const link = document.createElement("a");
      link.download = `otaku-index-${safeName}-${suffix}.png`;
      link.href = dataUrl;
      link.click();
      toast.success(`${format === "story" ? "Story" : format === "social" ? "Social" : "Card"} image downloaded`);
    } catch (err) {
      toast.error("Export failed");
    } finally {
      setExportingFormat(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-[#FF3B30]" /></div>;

  // Below the 10-completed threshold — every user needs to add more before
  // even the free teaser opens up. Show progress, not the paywall.
  if (data && !data.error && !data.teaser_eligible && !hasDeep) {
    return <BelowThresholdState completed={data.completed_count || 0} needed={data.free_persona_min_completed || 10} />;
  }

  if (!data || data.error) {
    return (
      <div className="mx-auto max-w-[1400px] px-6 py-16 md:px-10">
        <h1 className="font-display text-6xl uppercase text-white">No persona yet</h1>
        <p className="mt-3 text-neutral-500">{data?.error || "Add anime to unlock analysis."}</p>
      </div>
    );
  }

  const topGenres = [data.top_genre, ...(data.secondary_genres || [])];
  const totalRatings = Object.values(data.genre_breakdown || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="relative mx-auto max-w-[1600px] px-6 py-14 md:px-10">
      <div className="pointer-events-none absolute inset-0 radiating opacity-40" />
      <div className="relative">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Vol. 04 — Persona Analysis</p>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="share-persona-btn"
              onClick={handleShare}
              disabled={sharing}
              className="flex items-center gap-2 border border-white/20 bg-transparent px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-white hover:bg-[#FF3B30] hover:border-[#FF3B30] disabled:opacity-50"
            >
              {sharing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Share2 className="h-3 w-3" />}
              Share Link
            </button>
            <button
              data-testid="download-social-btn"
              onClick={() => exportCard("social")}
              disabled={exportingFormat !== null}
              title="Landscape 1200×630 — perfect for Twitter, LinkedIn, Facebook"
              className="flex items-center gap-2 border border-white/20 bg-white px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-black hover:bg-[#FF3B30] hover:text-white disabled:opacity-50"
            >
              {exportingFormat === "social" ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageDown className="h-3 w-3" />}
              Social <span className="opacity-60">1200×630</span>
            </button>
            <button
              data-testid="download-story-btn"
              onClick={() => exportCard("story")}
              disabled={exportingFormat !== null}
              title="Portrait 1080×1920 — perfect for IG / TikTok stories"
              className="flex items-center gap-2 border border-white/20 bg-transparent px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-white hover:bg-[#FF3B30] hover:border-[#FF3B30] disabled:opacity-50"
            >
              {exportingFormat === "story" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Smartphone className="h-3 w-3" />}
              Story <span className="opacity-60">1080×1920</span>
            </button>
          </div>
        </div>

        {shareUrl && (
          <div data-testid="share-url-panel" className="mt-4 flex items-center gap-3 border border-[#FF3B30]/40 bg-[#FF3B30]/5 px-4 py-3">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30]">Public link:</span>
            <code data-testid="share-url" className="flex-1 truncate font-mono text-xs text-white">{shareUrl}</code>
            <button
              onClick={copyExisting}
              data-testid="copy-share-url"
              className="flex items-center gap-1 border border-white/15 bg-black px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
            >
              {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        )}

        {/* Shareable card — this is the DOM node that gets exported */}
        <div ref={cardRef} className="mt-6 bg-[#050505]" data-testid="persona-card">
          <div className="border border-white/15 p-8 md:p-12 relative overflow-hidden">
            <div className="pointer-events-none absolute inset-0 radiating opacity-30" />
            <div className="relative">
              <div className="flex items-center gap-3 border-b border-white/10 pb-4">
                <Logo className="h-7 md:h-8" />
                <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-neutral-500">Persona Report</span>
              </div>

              <div className="mt-8">
                <p className="font-mono text-[11px] uppercase tracking-widest text-neutral-500">You are</p>
                <h1
                  data-testid="persona-title"
                  className="mt-2 font-display text-[3.5rem] uppercase leading-[0.9] tracking-tighter text-white md:text-[7rem]"
                >
                  {data.personality_type}
                </h1>
                <p className="mt-6 max-w-2xl text-lg leading-relaxed text-neutral-300">
                  {data.core_description}
                </p>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-6 border-t border-white/10 pt-6 md:grid-cols-4">
                <BadgeStat label="Top Genre" value={data.top_genre} accent />
                <BadgeStat label="Avg Rating" value={data.average_rating_given} />
                <BadgeStat label="Completion" value={`${data.completion_rate_percent}%`} />
                <BadgeStat label="Anime" value={data.total_anime_in_catalogue} />
              </div>

              <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                {hasDeep ? (
                  <>
                    <div className="border-l-2 border-[#FF3B30] pl-4">
                      <p className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">Rating Style</p>
                      <p className="mt-1 text-sm text-white">{data.rating_style}</p>
                    </div>
                    <div className="border-l-2 border-white/30 pl-4">
                      <p className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">Completion Style</p>
                      <p className="mt-1 text-sm text-white">{data.completion_style}</p>
                    </div>
                  </>
                ) : (
                  <div className="col-span-1 border border-dashed border-[#FF3B30]/40 bg-[#FF3B30]/5 px-4 py-3 md:col-span-2" data-testid="persona-locked-styles">
                    <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#FF3B30]">
                      <Lock className="h-3 w-3" /> Rating &amp; Completion styles locked
                    </p>
                    <p className="mt-1 text-xs text-neutral-400">Unlock deeper breakdowns for $2/mo.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {!hasDeep && <UpgradeSection />}

        {/* Detailed sections below (not part of card) — locked for free tier */}
        {hasDeep && (
          <>
            <section className="mt-14">
              <h2 className="font-display text-4xl uppercase tracking-widest text-white">Your Signal</h2>
              <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
                {topGenres.filter(Boolean).map((g, i) => (
                  <div key={g} className={`border p-6 ${i === 0 ? "border-[#FF3B30] bg-[#FF3B30]/5" : "border-white/15"}`}>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                      {TIER_LABELS[i] || "Genre"}
                    </p>
                    <p className="mt-3 font-display text-5xl uppercase text-white">{g}</p>
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                      {data.genre_breakdown[g] || 0} entries
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-14">
              <h2 className="font-display text-4xl uppercase tracking-widest text-white">Genre Map</h2>
              <div className="mt-6 space-y-2">
                {Object.entries(data.genre_breakdown || {}).map(([g, c]) => {
                  const pct = totalRatings ? (c / totalRatings) * 100 : 0;
                  return (
                    <div key={g} className="border border-white/10 bg-neutral-950 px-4 py-3">
                      <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-widest">
                        <span className="text-white">{g}</span>
                        <span className="text-neutral-500">{c} · {pct.toFixed(1)}%</span>
                      </div>
                      <div className="mt-2 h-1 w-full bg-white/5">
                        <div className="h-full bg-[#FF3B30]" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}
      </div>

      {/* Off-screen fixed-pixel share cards — rendered for PNG export.
          Kept in DOM (not display:none) so html-to-image can measure. */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          left: "-99999px",
          top: 0,
          pointerEvents: "none",
          opacity: 0,
        }}
      >
        <PersonaShareCard ref={socialRef} data={data} format="social" />
        <PersonaShareCard ref={storyRef} data={data} format="story" />
      </div>
    </div>
  );
}

const TIER_LABELS = ["Primary", "Secondary", "Tertiary"];

function BadgeStat({ label, value, accent }) {
  return (
    <div className={accent ? "border-l-2 border-[#FF3B30] pl-4" : "border-l-2 border-white/20 pl-4"}>
      <p className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">{label}</p>
      <p className="mt-1 font-display text-3xl uppercase text-white md:text-4xl">{value}</p>
    </div>
  );
}

// Progress state — user hasn't hit 10 completed yet, so even the free teaser
// isn't unlocked. Not a paywall — just a "keep going" nudge.
function BelowThresholdState({ completed, needed }) {
  const remaining = Math.max(0, needed - completed);
  const pct = Math.round((completed / needed) * 100);
  return (
    <div className="mx-auto max-w-[900px] px-6 py-20 md:px-10" data-testid="persona-below-threshold">
      <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Persona Analysis</p>
      <h1 className="mt-2 font-display text-6xl uppercase leading-[0.9] tracking-tight text-white md:text-8xl">
        {remaining} more<br />to unlock
      </h1>
      <p className="mt-4 max-w-xl text-sm text-neutral-400">
        Your first Persona Analysis is <span className="text-white">free</span>. Complete <span className="text-white">{needed} anime</span> and it unlocks automatically — no payment, no signup required.
      </p>
      <div className="mt-8 border border-white/15 bg-neutral-950 p-6">
        <div className="flex items-baseline justify-between font-mono text-[10px] uppercase tracking-widest">
          <span className="text-neutral-400">Completed</span>
          <span className="text-white">{completed} / {needed}</span>
        </div>
        <div className="mt-3 h-2 w-full bg-white/5">
          <div
            data-testid="persona-progress-bar"
            className="h-full bg-[#FF3B30] transition-all"
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>
      </div>
      <Link
        to="/"
        data-testid="persona-add-more-btn"
        className="mt-6 inline-flex items-center gap-2 border border-white/20 bg-[#FF3B30] px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
      >
        Add more anime
      </Link>
    </div>
  );
}

// Upsell surface shown UNDER the free persona teaser. Sells Persona Only
// primarily, with a link out to the full Pro Pass.
function UpgradeSection() {
  const [buying, setBuying] = useState(false);
  const buyPersonaOnly = async () => {
    setBuying(true);
    try {
      const { checkout_url } = await createCheckout("persona_only_monthly", getCreatorId());
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setBuying(false);
    }
  };
  return (
    <section
      data-testid="persona-upgrade-section"
      className="mt-14 border border-[#FF3B30]/40 bg-gradient-to-br from-[#FF3B30]/10 via-[#FF3B30]/5 to-transparent p-8 md:p-12"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-[#FF3B30]" />
        <p className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30]">// Upgrade to go deeper</p>
      </div>
      <h2 className="mt-2 font-display text-4xl uppercase leading-none tracking-tight text-white md:text-6xl">
        Re-run &amp; go<br />deeper — $2/mo
      </h2>
      <p className="mt-4 max-w-xl text-sm text-neutral-300">
        You&apos;ve seen your first Persona. Unlock re-runs as your catalogue grows, plus the deeper stats hidden above.
      </p>
      <ul className="mt-6 space-y-2">
        {PERSONA_ONLY_INCLUDES.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm text-neutral-200">
            <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#FF3B30]" />
            {f}
          </li>
        ))}
      </ul>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          data-testid="persona-upgrade-btn"
          onClick={buyPersonaOnly}
          disabled={buying}
          className="flex items-center gap-2 border border-white/20 bg-[#FF3B30] px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black disabled:opacity-50"
        >
          {buying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
          Unlock Persona · $2/mo
        </button>
        <Link
          to="/premium"
          data-testid="persona-see-pro-pass"
          className="font-mono text-[10px] uppercase tracking-widest text-white underline decoration-white/20 underline-offset-4 hover:text-[#FF3B30]"
        >
          Or see full Pro Pass benefits →
        </Link>
      </div>
    </section>
  );
}

